/**
 * Thin HTTP client for `/api/admin/billing/catalog` — the platform-admin
 * catalog surface (plans, credit packs, deployment billing settings).
 *
 * Separate from `billingApi` in ./index.ts because the audiences are disjoint:
 * that one is read by every tenant member, this one only by a platform admin
 * (`requirePlatformAdmin`). Keeping them apart means the tenant-facing bundle
 * never carries admin call sites.
 *
 * ── Money units on this wire ─────────────────────────────────────────
 *
 * Every price field is MINOR UNITS of the row's own `currency` (sen, cents,
 * yen, fils) — the same unit Stripe's `unit_amount` takes, so nothing on the
 * provider path converts. The UI is the only layer that divides, and it does
 * so through `formatCurrencyMinor` / `parseMajorToMinor` in
 * `@papan/types/currency`, never a literal `/100`. Budgets and credit are USD
 * MICRODOLLARS, which is a different unit again and never a charge.
 *
 * The column names still say `MyrCents` — historical, from when the deployment
 * was MYR-only. Read them as "minor units of `currency`".
 */
import { request } from '@papan/api-client'

const BASE = '/api/admin/billing/catalog'

/** The deployment singleton (migration 0148). */
/** One row of the rate timeline. */
export interface RateEpochView {
  id: string
  /** `null` for the GENESIS epoch — the rate in force before history existed,
   *  backfilled by 0172. Not "unknown". */
  effectiveFrom: string | null
  isGenesis: boolean
  currency: string
  creditRateMinorPerUsd: number
  /** Scheduled, not yet in force. The only rows that can be cancelled. */
  pending: boolean
  actorEmail: string | null
  note: string | null
}


export interface BillingSettingsView {
  id: string
  /** Lowercase ISO-4217. The currency this deployment charges in.
   *
   *  Codex round 20 F16: renamed from `currency` to match what the
   *  server ACTUALLY returns after 0177's `billing_settings.currency`
   *  → `billing_currency` column rename. The repo's `.get()` returns
   *  `billingCurrency`, so the pre-rename DTO field was `undefined` at
   *  runtime and CatalogAdmin crashed with `Cannot read properties of
   *  undefined (reading 'toLowerCase')` when `priceFor(plan,
   *  settings.currency)` tried to lowercase it. The PUT `/settings`
   *  route still accepts `currency` on the WRITE body (see the
   *  server-side normalization in `updateSettings`), so callers only
   *  need this rename on the READ shape. */
  billingCurrency: string
  /**
   * Minor units charged per US$1.00 of LLM credit — THE GROSS MARGIN on AI
   * usage, not a conversion rate. 550 = RM5.50 per $1.
   */
  creditRateMinorPerUsd: number
  /** Self-serve custom-amount band, minor units. */
  purchaseFloorMinor: number
  purchaseCeilingMinor: number
  updatedAt: string
}

/**
 * A plan's money in ONE currency (`billing_plan_prices`).
 *
 * `allowance = base_pool + max(0, seats − included_seats) × extra_seat_allowance`,
 * and `price = base_price + max(0, seats − included_seats) × extra_seat_price`.
 * The two units on the row are deliberate: `*Minor` is what a provider moves,
 * `*BillingMicro` is the granted allowance in the canonical internal unit,
 * because one LLM turn is far below a minor unit and would round to zero.
 */
export interface AdminPlanPriceView {
  planId: string
  /** Lowercase ISO-4217. */
  currency: string
  basePriceMinor: number
  extraSeatPriceMinor: number
  basePoolBillingMicro: number
  extraSeatAllowanceBillingMicro: number
  /** Overage-pool ceiling, sibling of `basePoolBillingMicro` (0190 Phase 1). */
  extraPoolBillingMicro: number
  /** Grace-pool ceiling, sibling of `basePoolBillingMicro` (0190 Phase 1). */
  gracePoolBillingMicro: number
  /**
   * CHARGEABLE right now — a Stripe Price exists for this currency AND it was
   * minted for the amount the catalogue currently states.
   *
   * Not `!!priceId`: a Stripe Price is immutable, so editing the price leaves
   * the stored id charging the OLD amount. `syncState` below distinguishes the
   * two unchargeable reasons, which want different words on screen.
   */
  syncedToProvider: boolean
  /**
   * `synced` — chargeable.
   * `dirty`  — the price changed and has not been published; the live Price
   *            still charges the previous amount, so checkout is refused.
   * `unsynced` — no Price has ever been minted for this (plan, currency).
   */
  syncState: 'synced' | 'dirty' | 'unsynced'
}

export interface AdminPlanView {
  id: string
  name: string
  audience: 'personal' | 'organization' | 'both'
  /**
   * Every currency this plan is sold in. EMPTY = unpriced, which is a refusing
   * state rather than a free one: an unpriced plan is not listed to customers,
   * cannot be subscribed to, and makes `applyEntitlements` raise.
   */
  prices: AdminPlanPriceView[]
  /** Seats the base price and base pool cover before the per-seat terms apply. */
  includedSeats: number
  /**
   * The plan's seat ceiling. `null` = unlimited (the top of the inherit chain);
   * a number (incl. `0` = nobody) is a hard limit. Resolved LIVE per tenant via
   * `resolveMaxSeats(tenants.max_seats_override, this)` — never copied — so a
   * change here reaches inheriting tenants immediately.
   */
  maxSeats: number | null
  storageQuotaBytes: number
  agentConcurrencyCap: number | null
  /**
   * The plan's RELATIVE share of a worker compute lane while tenants contend
   * for it (#539, 0210). Not a cap: only the ratio between plans means
   * anything, and every plan at the same number is plain round-robin. NOT NULL
   * server-side, defaulting to 1.
   */
  fairnessWeight: number
  features: Record<string, unknown>
  isActive: boolean
  sortOrder: number
  /**
   * The preset's billing rhythm (D17) — how often the seat price is charged, and
   * how far "Record payment" advances a manually-billed customer's paid-through
   * date. NOT NULL server-side, defaulting to 1 month.
   *
   * INVOICING ONLY: the AI allowance still resets on the calendar month, because
   * that is the metering spine's storage format rather than a setting.
   */
  cycleCount: number
  cycleUnit: 'day' | 'week' | 'month'
  /**
   * Tenants EXPLICITLY on this plan — a `platform_plan_override` plus live
   * subscriptions. NOT the implicit free-plan fallthrough (that would need the
   * per-tenant effective-plan ladder). Advisory; 0 if the count query failed.
   */
  assignedTenantCount: number
}

export interface AdminPackView {
  id: string
  name: string
  currency: string
  /** Pack price, minor units. */
  priceMinor: number
  /**
   * Credit granted on purchase, billing-currency MICRO — the pack's own FACE
   * VALUE, derived from its price and the currency's exponent, not from a rate.
   *
   * There is no `rateMyrCentsPerUsd` any more, and its absence is the feature:
   * a pack's grant was a USD quantity derived through the credit rate, which is
   * what made a rate change able to strand every active pack at its old grant
   * (the whole `packAction` / `PACKS_NEED_REVIEW` apparatus). A face-value pack
   * has nothing to re-derive.
   */
  creditBillingMicro: number
  isActive: boolean
  sortOrder: number
  syncedToProvider: boolean
}

/** The body of `PUT /plans/:id/prices/:currency`. Every amount is required —
 *  each has a meaningful zero, so a partial write would leave a stale one. */
export interface PlanPriceBody {
  basePriceMinor: number
  extraSeatPriceMinor: number
  basePoolBillingMicro: number
  extraSeatAllowanceBillingMicro: number
  /** Overage-pool ceiling, sibling of `basePoolBillingMicro` (0190 Phase 1). */
  extraPoolBillingMicro: number
  /** Grace-pool ceiling, sibling of `basePoolBillingMicro` (0190 Phase 1). */
  gracePoolBillingMicro: number
}

export interface PlanPatch {
  name?: string
  audience?: string
  includedSeats?: number
  /** null = unlimited, a number = ceiling, absent = leave alone. */
  maxSeats?: number | null
  storageQuotaBytes?: number
  agentConcurrencyCap?: number | null
  /** Relative compute share, 1..100. NOT NULL server-side — there is no
   *  "unset"; absent means leave alone. */
  fairnessWeight?: number
  isActive?: boolean
  sortOrder?: number
  /** Both or neither — the route refuses one without the other. */
  cycleCount?: number
  cycleUnit?: 'day' | 'week' | 'month'
  /**
   * Plan feature flags. Sent as a WHOLE object — the route replaces rather than
   * merges — so a caller editing one key must spread the plan's existing
   * features or it will drop the others.
   *
   * `models` is the plan's AI model list; absent or empty means the plan
   * includes every model the tenant can otherwise see.
   */
  features?: Record<string, unknown>
  /** TOP-LEVEL key merge over `features`: absent key = leave alone, `null` = delete.
   *  NOT recursive — a sub-object is REPLACED whole, so send the complete sub-object.
   *  Prefer this over `features` from any editor section — three sections write this
   *  blob, and a whole-object replace from a stale render silently reverts a sibling
   *  (the collision lands on `canCreateContent`, the write freeze). Merged server-side
   *  in one UPDATE, so it is safe across two tabs too. */
  featuresPatch?: Record<string, unknown>
}

export interface PackPatch {
  name?: string
  currency?: string
  priceMinor?: number
  isActive?: boolean
  sortOrder?: number
}

/** What a sync reports back per object. */
export interface SyncResult {
  plans: unknown
  packs: unknown
}

/**
 * Present on a plan PATCH that changed something `applyEntitlements`
 * materializes onto tenants (catalogue A4).
 *
 * Entitlements are written onto the tenant at subscription events, so an edited
 * plan does not reach existing subscribers until something re-triggers that.
 * The failure is SILENT — the operator sees the new number here and no
 * customer's cap moves — which is why the response says so rather than leaving
 * the screen to look successful.
 */
export interface EntitlementsStale {
  /** Which grant-affecting fields changed. */
  fields: string[]
  /** Live subscribers now running on the old numbers. */
  tenants: number
}

export const catalogAdminApi = {
  /** Settings + whether billing is on (so the UI can explain an inert Publish
   *  button instead of letting it fail on click). */
  getSettings: () =>
    request<{
      settings: BillingSettingsView
      billingEnabled: boolean
      supportedCurrencies: string[]
      /** 'provider' | 'manual' — the plan editor hides the extra-seat provider
       *  fields under manual billing. */
      planAuthority: 'provider' | 'manual'
    }>(`${BASE}/settings`),

  /**
   * Partial — an omitted field is left alone. Floor/ceiling are validated
   * server-side against the MERGED state, so sending one of the pair is safe.
   *
   * ── THE RATE AND THE CURRENCY ARE EPOCH-BACKED (0172) ─────────────────
   *
   * Changing either APPENDS an epoch rather than patching a column.
   * `effectiveFrom` schedules the change; omitted means now, and the past is
   * refused.
   *
   * There is no `packAction` and no `409 PACKS_NEED_REVIEW` (0177): a credit
   * pack grants its own face value, so a rate change cannot restate what any
   * pack grants and there is nothing to decide.
   */
  updateSettings: (patch: {
    /** Write-side wire field name — the PUT `/settings` handler
     *  accepts `currency` on the body. Codex F16 (round 20): the READ
     *  side of `BillingSettingsView` uses `billingCurrency` (matches
     *  the 0177 column rename); the WRITE side keeps `currency`
     *  because the server-side normalizer maps it to the epoch. Do
     *  not add `billingCurrency` here — the server won't recognise it. */
    currency?: string
    creditRateMinorPerUsd?: number
    purchaseFloorMinor?: number
    purchaseCeilingMinor?: number
    /** ISO-8601. Must not be in the past — a backdated epoch restates history. */
    effectiveFrom?: string
  }) =>
    request<{
      settings: BillingSettingsView
      epoch?: { effectiveFrom: string; scheduled: boolean }
    }>(`${BASE}/settings`, {
      method: 'PUT',
      body: JSON.stringify(patch),
    }),

  /**
   * The rate timeline, newest first — the audit trail `billing_settings` never
   * had, plus whatever is scheduled.
   */
  getRateHistory: () =>
    request<{ epochs: RateEpochView[] }>(`${BASE}/settings/rate-history`),

  /**
   * Cancel a PENDING epoch.
   *
   * The only deletion this system permits: an epoch that has taken effect
   * governed real charges and is refused with `409 EPOCH_ALREADY_EFFECTIVE`.
   */
  cancelRateEpoch: (id: string) =>
    request<{ ok: true }>(`${BASE}/settings/rate-history/${encodeURIComponent(id)}`, {
      method: 'DELETE',
    }),

  listPlans: () =>
    request<{ plans: AdminPlanView[]; billingCurrency: string; billingEnabled: boolean }>(
      `${BASE}/plans`),

  /** The id is generated server-side from the name (a permanent slug); the
   *  response carries the real id. Any `id` sent is ignored by the route. */
  createPlan: (plan: PlanPatch & { name: string }) =>
    request<{ plan: AdminPlanView }>(`${BASE}/plans`, {
      method: 'POST',
      body: JSON.stringify(plan),
    }),

  updatePlan: (id: string, patch: PlanPatch) =>
    request<{ plan: AdminPlanView; entitlementsStale?: EntitlementsStale }>(
      `${BASE}/plans/${encodeURIComponent(id)}`,
      { method: 'PATCH', body: JSON.stringify(patch) },
    ),

  /**
   * Set what a plan costs and grants in ONE currency.
   *
   * Does NOT re-mint the Stripe Price — `sync()` is the deliberate second step,
   * because a re-mint orphans the old Price for existing subscribers. The
   * response carries how many live subscribers are now on the OLD allowance:
   * entitlements are materialised onto tenants at subscription events, so an
   * edit here reaches nobody until one fires.
   */
  setPlanPrice: (id: string, currency: string, body: PlanPriceBody) =>
    request<{ price: AdminPlanPriceView; staleSubscribers: number }>(
      `${BASE}/plans/${encodeURIComponent(id)}/prices/${encodeURIComponent(currency)}`,
      { method: 'PUT', body: JSON.stringify(body) },
    ),

  /** Withdraw a plan from one currency. Refused for an ACTIVE plan in the
   *  deployment's own billing currency — that combination refuses every
   *  entitlement on the plan. */
  removePlanPrice: (id: string, currency: string) =>
    request<{ ok: true }>(
      `${BASE}/plans/${encodeURIComponent(id)}/prices/${encodeURIComponent(currency)}`,
      { method: 'DELETE' },
    ),

  /**
   * Who a cycle change would reprice, BEFORE it is made.
   *
   * The price is stated per period, so monthly → weekly multiplies what a
   * customer pays by ~4.3 with the number on screen unchanged and no audit row
   * saying the amount moved. This is what the confirmation is built from; it
   * never auto-converts the amount.
   */
  planCycleImpact: (id: string) =>
    request<{
      planId: string
      cycleCount: number
      cycleUnit: 'day' | 'week' | 'month'
      /** `null` when the plan carries no price in the billing currency. */
      seatPriceMinor: number | null
      currency: string
      inheriting: number
      overridden: number
    }>(`${BASE}/plans/${encodeURIComponent(id)}/cycle-impact`),

  /**
   * LLM prices at one layer, IN ONE CURRENCY (0167, currency axis 0186), with
   * the shipped default beside each.
   *
   * Returns EVERY sellable model, not only the overridden ones: an editor
   * listing only what exists could never add anything, and "which models am I
   * not pricing?" is what an operator opens this to answer.
   *
   * `currency` defaults server-side to `'usd'` when omitted — the only
   * currency this editor has ever written. A model may now carry a row PER
   * currency at this scope, so the server keys its response to the ONE
   * currency requested rather than collapsing every currency into one map.
   */
  listModelPricing: (scope: 'global' | 'plan' | 'tenant', scopeId?: string, currency?: string) =>
    request<{
      scope: string
      scopeId: string | null
      currency: string
      models: Array<{
        modelId: string
        label: string
        /** USD microdollars per 1M, from `providers.ts`. What clearing restores. */
        shipped: { input: number; output: number } | null
        override: {
          inputUsdMicroPer1m: number
          outputUsdMicroPer1m: number
          cacheReadUsdMicroPer1m: number | null
          cacheWriteUsdMicroPer1m: number | null
          currency: string
        } | null
      }>
    }>(`${BASE}/model-pricing?scope=${scope}${scopeId ? `&scopeId=${encodeURIComponent(scopeId)}` : ''}${currency ? `&currency=${encodeURIComponent(currency)}` : ''}`),

  /**
   * Set or clear one model's price at one layer, in one currency.
   *
   * Both rates `null` CLEARS the override — a distinct action from setting them
   * to 0, which is a real price (a model given away). `currency` (default
   * `'usd'`) is which currency `input`/`outputUsdMicroPer1m` are actually
   * denominated in; clearing only removes the row for THIS currency — a model
   * priced in both MYR and USD needs each cleared independently.
   */
  setModelPricing: (input: {
    scope: 'global' | 'plan' | 'tenant'
    scopeId?: string
    modelId: string
    currency?: string
    inputUsdMicroPer1m: number | null
    outputUsdMicroPer1m: number | null
  }) =>
    request<{ cleared?: boolean; removed?: boolean; override?: unknown }>(
      `${BASE}/model-pricing`,
      { method: 'PUT', body: JSON.stringify(input) },
    ),

  listPacks: () =>
    request<{ packs: AdminPackView[]; billingEnabled: boolean }>(`${BASE}/credit-packs`),

  /** `creditBillingMicro` is DERIVED server-side from the price and the current
   *  rate — deliberately not accepted from here, so the advertised credit and
   *  the credited amount cannot disagree. */
  createPack: (pack: PackPatch & { id: string; name: string; priceMinor: number }) =>
    request<{ pack: AdminPackView }>(`${BASE}/credit-packs`, {
      method: 'POST',
      body: JSON.stringify(pack),
    }),

  updatePack: (id: string, patch: PackPatch) =>
    request<{ pack: AdminPackView }>(`${BASE}/credit-packs/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body: JSON.stringify(patch),
    }),

  /** Push local rows into Stripe (idempotent). The only flag-gated route here —
   *  it is the one that writes to the provider. `scope: 'packs'` publishes ONLY
   *  credit packs (the manual-billing console's Publish; plans never subscribe
   *  via Stripe there). */
  sync: (opts?: { scope?: 'packs' }) =>
    request<SyncResult>(
      `${BASE}/sync${opts?.scope ? `?scope=${opts.scope}` : ''}`,
      { method: 'POST' },
    ),

  /** Re-materialize one tenant's entitlements after a grant-affecting plan
   *  edit. Per-tenant by design; the fleet-wide form is a script. */
  reapplyEntitlements: (tenantId: string) =>
    request<{ applied: unknown }>(
      `${BASE}/reapply-entitlements/${encodeURIComponent(tenantId)}`,
      { method: 'POST' },
    ),

  // ── Per-plan metered feature pricing ──────────────────────────────────
  // These hit the plan feature-pricing routes on the ADMIN router (served by
  // `features/files/api/admin.ts`), NOT the catalog `${BASE}`. Folded into this
  // client so the price book's plan editor can edit metered rates per plan
  // without `@papan/billing` importing `@papan/orgs` (which would close a cycle).
  /** `currency` (default `'usd'` server-side) selects ONE currency's grid —
   *  a feature may carry a row per currency at this plan (0187). */
  getPlanFeaturePricing: (planId: string, currency?: string) =>
    request<{ planId: string; planName: string; currency: string; pricing: PlanFeaturePricingRow[] }>(
      `/api/admin/plans/${encodeURIComponent(planId)}/feature-pricing${currency ? `?currency=${encodeURIComponent(currency)}` : ''}`,
    ),
  /** Upsert one per-plan metered override. `baseRateUsd: null` inherits the
   *  rate; only `chargeable`/`baseRateUsd` are touched (tiers/mode preserved).
   *  `currency` (default `'usd'`) is which currency this rate is denominated
   *  in — a plan may hold both a billing-currency row and a USD anchor. */
  setPlanFeaturePricing: (
    planId: string,
    feature: string,
    values: { chargeable?: boolean | null; baseRateUsd?: number | null; currency?: string },
  ) =>
    request<{ success: boolean }>(
      `/api/admin/plans/${encodeURIComponent(planId)}/feature-pricing`,
      { method: 'PUT', body: JSON.stringify({ feature, ...values }) },
    ),
  /** Remove the per-plan override IN ONE CURRENCY (default `'usd'`),
   *  reverting that currency's rate to the deployment default. A plan holding
   *  both a billing-currency and a USD row must clear each independently. */
  clearPlanFeaturePricing: (planId: string, feature: string, currency?: string) =>
    request<{ success: boolean }>(
      `/api/admin/plans/${encodeURIComponent(planId)}/feature-pricing/${encodeURIComponent(feature)}${currency ? `?currency=${encodeURIComponent(currency)}` : ''}`,
      { method: 'DELETE' },
    ),

  // ── Per-TENANT metered feature pricing (same shape, tenant route) ──────
  // The tenant GET adds a `planLayer` (the plan rung this tenant inherits from),
  // so a tenant-scope editor shows "inherits the plan" rather than "the global".
  /** `currency` (default `'usd'` server-side) selects ONE currency's grid. */
  getTenantFeaturePricing: (tenantId: string, currency?: string) =>
    request<{ tenantId: string; currency: string; pricing: PlanFeaturePricingRow[] }>(
      `/api/admin/tenants/${encodeURIComponent(tenantId)}/feature-pricing${currency ? `?currency=${encodeURIComponent(currency)}` : ''}`,
    ),
  setTenantFeaturePricing: (
    tenantId: string,
    feature: string,
    values: { chargeable?: boolean | null; baseRateUsd?: number | null; currency?: string },
  ) =>
    request<{ success: boolean }>(
      `/api/admin/tenants/${encodeURIComponent(tenantId)}/feature-pricing`,
      { method: 'PUT', body: JSON.stringify({ feature, ...values }) },
    ),
  /** Remove the per-tenant override IN ONE CURRENCY (default `'usd'`). */
  clearTenantFeaturePricing: (tenantId: string, feature: string, currency?: string) =>
    request<{ success: boolean }>(
      `/api/admin/tenants/${encodeURIComponent(tenantId)}/feature-pricing/${encodeURIComponent(feature)}${currency ? `?currency=${encodeURIComponent(currency)}` : ''}`,
      { method: 'DELETE' },
    ),
}

/** One priceable metered feature's resolved pricing, for a plan OR a tenant.
 *  `unitRateUsd` / `baseRateUsd` are USD microdollars. */
export interface PlanFeaturePricingRow {
  feature: string
  global: { feature: string; chargeable: boolean; unitRateUsd: number; unit: string | null; currency: string } | null
  override: { mode: string; chargeable: boolean | null; baseRateUsd: number | null; currency: string } | null
  /** TENANT scope only: the plan rung this tenant inherits from when it has no
   *  override of its own (absent for plan scope, where the base IS the global). */
  planLayer?: { mode: string; chargeable: boolean | null; baseRateUsd: number | null; currency: string } | null
  source: 'override' | 'global' | 'plan'
}
